import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralConversationViewRole } from '../../helpers/DataStructures';
import Busy from '../../components/Busy';
import ReferralConversationCounterpartyHeader from './ReferralConversationCounterpartyHeader';
import ReferralConversationDetail from './ReferralConversationDetail';
import ReferralRouteShell from './ReferralRouteShell';

interface AdminReferralConversationPageProps {
  viewAsRole: ReferralConversationViewRole;
}

const AdminReferralConversationPage: React.FC<AdminReferralConversationPageProps> = ({
  viewAsRole,
}) => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const { referralId } = useParams();
  const parsedReferralId = referralId ? Number(referralId) : NaN;
  const isValidReferralId = Number.isInteger(parsedReferralId) && parsedReferralId > 0;
  const isOfferOwner = viewAsRole === 'offerer';

  const { isLoading, isError, data, error } = useQuery(
    [CacheKeys.ADMIN_REFERRAL_BY_ID, parsedReferralId],
    () => apiServiceInstance.getAdminReferralById(parsedReferralId),
    { enabled: isValidReferralId, retry: false }
  );

  const errorMessage =
    error instanceof Error && error.message ? error.message : 'Unable to load this referral.';

  return (
    <ReferralRouteShell
      backTo="/admins"
      headerLeft={
        !isValidReferralId || isLoading || isError || !data ? null : (
          <ReferralConversationCounterpartyHeader referral={data} isOfferOwner={isOfferOwner} />
        )
      }
      maxWidth={false}
    >
      {!isValidReferralId ? (
        <Typography>Referral unavailable.</Typography>
      ) : isLoading ? (
        <Busy message="Loading referral..." />
      ) : isError ? (
        <Typography>{errorMessage}</Typography>
      ) : data === null ? (
        <Typography>Referral unavailable.</Typography>
      ) : (
        <ReferralConversationDetail
          conversationQueryKey={[CacheKeys.ADMIN_REFERRAL_CONVERSATION, data.id, viewAsRole]}
          key={`${data.id}-${viewAsRole}-admin`}
          loadConversation={(targetReferralId) =>
            apiServiceInstance.getAdminReferralConversation(targetReferralId, viewAsRole)
          }
          readOnly
          referral={data}
          showCounterpartyHeader={false}
          suppressParticipantRefresh
          viewAsRole={viewAsRole}
        />
      )}
    </ReferralRouteShell>
  );
};

export default AdminReferralConversationPage;
